import { Tooltip } from "@/base";
import { useFormControllerContext } from "@/contexts";
import {
    Button,
    cn,
    Autocomplete as NextUiAutocomplete,
    AutocompleteItem as NextUiAutocompleteItem,
    type AutocompleteProps as NextUiAutocompleteProps,
} from "@heroui/react";
import {
    safelyAppendSuffixToString,
    safelyRemoveSuffixFromString,
} from "@sr-partner-portal/common";
import { IconBulb, IconInfoCircle } from "@tabler/icons-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
    type FieldValues,
    type UseControllerProps,
    useController as useFieldController,
} from "react-hook-form";

type SharedAutocompleteProps = {
    label?: string;
    placeholder?: string;
    isAutofocusing?: boolean;
    autoCompleteItems?: string[];
    isDisabled?: boolean;
    isRequired?: boolean;
    description?: ReactNode;
    hint?: ReactNode;
    endContent?: NextUiAutocompleteProps["endContent"];
    suffix?: string;
    withoutSelector?: boolean;
    isInvalid?: boolean;
    errorMessage?: string;
    isViewOnly?: boolean;
    handleOnChange?: (value: string) => void;
};

type BaseAutocompleteProps = {
    value?: string;
} & SharedAutocompleteProps;

type AutocompleteProps<T extends FieldValues> = {
    fieldName: UseControllerProps<T>["name"];
} & SharedAutocompleteProps;

const BaseAutocomplete = ({
    description,
    isAutofocusing,
    isDisabled,
    label,
    isRequired,
    placeholder,
    autoCompleteItems = [],
    endContent,
    hint,
    suffix,
    withoutSelector,
    value,
    isInvalid,
    errorMessage,
    isViewOnly,
    handleOnChange,
}: BaseAutocompleteProps) => {
    const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false);
    const items = useMemo(
        () => autoCompleteItems?.map((item) => ({ key: item, label: item })),
        [autoCompleteItems],
    );

    const handleCopy = useCallback(async () => {
        try {
            let formattedValue = value;

            if (suffix && value && !value.endsWith(suffix)) {
                formattedValue = safelyAppendSuffixToString(value, suffix);
            }

            await navigator.clipboard.writeText(formattedValue);
            setIsCopyTooltipOpen(true);
            setTimeout(() => setIsCopyTooltipOpen(false), 1000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    }, [value, suffix]);

    return (
        <div className="relative">
            <NextUiAutocomplete
                defaultItems={items}
                autoFocus={isAutofocusing}
                isDisabled={isDisabled}
                disabled={isDisabled || isViewOnly}
                className={cn(isViewOnly && "opacity-70")}
                placeholder={placeholder}
                fullWidth={true}
                isInvalid={isInvalid}
                errorMessage={errorMessage}
                label={label}
                onSelectionChange={handleOnChange}
                onInputChange={handleOnChange}
                value={value}
                inputValue={value}
                isRequired={isRequired}
                allowsCustomValue={true}
                selectorIcon={withoutSelector ? null : undefined}
                selectorButtonProps={
                    withoutSelector && {
                        className: "hidden",
                    }
                }
                endContent={
                    <div className="mr-2 flex shrink-0 items-center gap-1">
                        {endContent}
                        {suffix}
                        {description && (
                            <Tooltip color="primary" content={description}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                >
                                    <IconInfoCircle stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        {hint && (
                            <Tooltip color="warning" content={hint}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                >
                                    <IconBulb stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                }
            >
                {(item: { key: string; label: string }) => (
                    <NextUiAutocompleteItem key={item.key}>
                        {item.label}
                    </NextUiAutocompleteItem>
                )}
            </NextUiAutocomplete>
            {value && isViewOnly && (
                <Tooltip
                    content="Kopiert"
                    placement="top-end"
                    closeDelay={2000}
                    isOpen={isCopyTooltipOpen}
                >
                    <button
                        type="button"
                        className="absolute top-0 left-0 h-full w-full"
                        onClick={handleCopy}
                    />
                </Tooltip>
            )}
        </div>
    );
};

const RegisteredAutocomplete = <T extends FieldValues>({
    description,
    fieldName,
    isAutofocusing,
    isDisabled,
    label,
    isRequired,
    placeholder,
    autoCompleteItems,
    endContent,
    hint,
    suffix,
    withoutSelector,
    isViewOnly,
}: AutocompleteProps<T>) => {
    const formController = useFormControllerContext();

    const fieldController = useFieldController<T>({
        control: formController.control,
        name: fieldName,
    });

    const handleOnChange = useCallback(
        (value: string) => {
            if (!fieldController) {
                return;
            }

            let modifiedValue = value;

            if (suffix && value && !value.endsWith(suffix)) {
                modifiedValue = safelyAppendSuffixToString(value, suffix);
            }

            fieldController.field.onChange(modifiedValue);
        },
        [fieldController, suffix],
    );

    const value = fieldController?.field.value || "";

    return (
        <BaseAutocomplete
            autoCompleteItems={autoCompleteItems}
            isAutofocusing={isAutofocusing}
            isDisabled={isDisabled}
            placeholder={placeholder}
            isInvalid={!!fieldController.fieldState.error}
            errorMessage={fieldController.fieldState.error?.message}
            label={label}
            handleOnChange={handleOnChange}
            value={suffix ? safelyRemoveSuffixFromString(value, suffix) : value}
            suffix={suffix}
            isRequired={isRequired}
            withoutSelector={withoutSelector}
            endContent={endContent}
            hint={hint}
            description={description}
            isViewOnly={isViewOnly}
        />
    );
};

const Autocomplete = <T extends FieldValues>({
    shouldRegister = true,
    ...otherProps
}: AutocompleteProps<T> & { shouldRegister?: boolean }) => {
    return shouldRegister ? (
        <RegisteredAutocomplete {...otherProps} />
    ) : (
        <BaseAutocomplete {...otherProps} />
    );
};

export { Autocomplete };
